import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { ModalContext } from "contexts/modalContext";
import { PaymentLinkIcon } from "../PaymentLink/PaymentLinkIcon";
import gtmEvents from "utils/gtmEvents";
import __ from "utils/i18n";
import "./styles.scss";

export const PaymentLinkDropdown = ({ showIndividual = true, showMassive = true }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const toggleRef = useRef(null);

  const toggle = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Detectar si es mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Calcular la posición del dropdown cuando se abre (solo desktop)
  useEffect(() => {
    if (dropdownOpen && toggleRef.current && !isMobile) {
      const rect = toggleRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8, // 8px debajo del botón
        left: rect.left,
      });
    }
  }, [dropdownOpen, isMobile]);

  // Cerrar dropdown al hacer clic fuera (solo desktop)
  useEffect(() => {
    if (!dropdownOpen || isMobile) return;

    const handleClickOutside = (event) => {
      if (toggleRef.current && !toggleRef.current.contains(event.target)) {
        // Verificar si el clic fue dentro del menú portal
        const menu = document.querySelector('.payment-link-dropdown-menu');
        if (!menu || !menu.contains(event.target)) {
          setDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, isMobile]);

  // Contenido del menú (reutilizable)
  const renderMenuContent = (context) => (
    <>
      {showIndividual && (
        <button
          type="button"
          className="dropdown-item payment-link-dropdown-item"
          onClick={() => {
            gtmEvents.paymentLinkButtonClicked();
            context.openModal("PaymentLinkModal");
            setDropdownOpen(false);
          }}
        >
          <div className="payment-link-option">
            <div className="payment-link-option-title">
              <i className="mdi mdi-link-variant mr-2" />
              {__("dashboard.sections.paymentLink.dropdown.individual.title")}
            </div>
            <div className="payment-link-option-description">
              {__("dashboard.sections.paymentLink.dropdown.individual.description")}
            </div>
          </div>
        </button>
      )}
      {showIndividual && showMassive && <div className="dropdown-divider" />}
      {showMassive && (
        <button
          type="button"
          className="dropdown-item payment-link-dropdown-item"
          onClick={() => {
            gtmEvents.paymentLinkButtonClicked();
            context.openModal("MassivePaymentLinkModal");
            setDropdownOpen(false);
          }}
        >
          <div className="payment-link-option">
            <div className="payment-link-option-title">
              <i className="mdi mdi-file-multiple mr-2" />
              {__("dashboard.sections.paymentLink.dropdown.massive.title")}
            </div>
            <div className="payment-link-option-description">
              {__("dashboard.sections.paymentLink.dropdown.massive.description")}
            </div>
          </div>
        </button>
      )}
    </>
  );

  return (
    <ModalContext.Consumer>
      {(context) => (
        <>
          <li 
            className="dashboard-header__item-container nav-item"
            onClick={(e) => e.stopPropagation()} // Evita que el click del Nav cierre el dropdown
            ref={toggleRef}
          >
            <button
              className={`btn btn-primary btn-sm dashboard-header__button payment-link-radius ${dropdownOpen ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
            >
              <PaymentLinkIcon /> {__("dashboard.sections.paymentLink.title")}
              <i className={`mdi mdi-chevron-down ml-1 ${dropdownOpen ? 'rotate' : ''}`} />
            </button>
            
            {/* En mobile, renderizar el menú dentro del li (collapse) */}
            {isMobile && dropdownOpen && (
              <div className="payment-link-dropdown-menu payment-link-dropdown-menu--mobile">
                {renderMenuContent(context)}
              </div>
            )}
          </li>
          
          {/* En desktop, renderizar el menú como portal fuera del DOM */}
          {!isMobile && dropdownOpen && ReactDOM.createPortal(
            <div
              className="payment-link-dropdown-menu dropdown-menu show"
              style={{
                position: 'fixed',
                top: `54px`,
                left: `${menuPosition.left}px`,
                zIndex: 1050,
              }}
            >
              {renderMenuContent(context)}
            </div>,
            document.body
          )}
        </>
      )}
    </ModalContext.Consumer>
  );
};
